/**
 * Scheduling UI role helpers — ACL display names from /users/me `acl_roles`
 * (Role.name), not the legacy User.role enum (`doctor` / `admin`).
 *
 * Backend still enforces appointment.read / appointment.write.
 */

/** Clinical providers who can own a calendar (match authz defaults Role.name). */
export const CLINICAL_PROVIDER_ROLE_NAMES = new Set([
  'Midwife',
  'Sonographer',
  'GP',
  'OB',
  'MFM Specialist',
]);

/** Front-desk / admin roles that manage the facility-wide schedule. */
export const SCHEDULER_ROLE_NAMES = new Set([
  'Receptionist',
  'Org Admin',
  'Facility Admin',
]);

export type SchedulingUserLike = {
  id?: string | number | null;
  acl_roles?: string[] | null;
  is_tenant_owner?: boolean | null;
  data?: SchedulingUserLike | null;
};

function unwrapUser(user: SchedulingUserLike | null | undefined): SchedulingUserLike | null {
  if (!user) return null;
  if (user.data && (user.data.id != null || user.data.acl_roles)) {
    return user.data;
  }
  return user;
}

export function getAclRoleNames(user: SchedulingUserLike | null | undefined): string[] {
  const u = unwrapUser(user);
  return Array.isArray(u?.acl_roles) ? u!.acl_roles! : [];
}

export function hasAnyAclRole(
  user: SchedulingUserLike | null | undefined,
  names: Set<string>
): boolean {
  return getAclRoleNames(user).some((name) => names.has(name));
}

/** True when the user holds a clinical provider ACL role. */
export function isClinicalProviderUser(
  user: SchedulingUserLike | null | undefined
): boolean {
  return hasAnyAclRole(user, CLINICAL_PROVIDER_ROLE_NAMES);
}

/**
 * Receptionist / facility|org admin / tenant owner — see facility agenda +
 * assignee pickers rather than a locked personal calendar.
 */
export function isSchedulerUser(user: SchedulingUserLike | null | undefined): boolean {
  const u = unwrapUser(user);
  if (u?.is_tenant_owner) return true;
  return hasAnyAclRole(user, SCHEDULER_ROLE_NAMES);
}

/**
 * Personal calendar default: clinical provider and not a scheduler role.
 * Schedulers always work the facility queue even if they also have a clinical role.
 */
export function usesPersonalCalendar(
  user: SchedulingUserLike | null | undefined
): boolean {
  return isClinicalProviderUser(user) && !isSchedulerUser(user);
}

/** Show staff filter / multi-assignee agenda controls. */
export function canFilterStaffCalendar(
  user: SchedulingUserLike | null | undefined
): boolean {
  return !usesPersonalCalendar(user);
}

/** Booking form must pick an assignee (not auto-self). */
export function needsAssigneePicker(
  user: SchedulingUserLike | null | undefined
): boolean {
  return !usesPersonalCalendar(user);
}
